/**
 * The board-work modules, fetched the first time they are wanted.
 *
 * Placement, routing, pouring and the congestion map are the heaviest thing in
 * the program and none of it is needed to open a drawing. Statically imported
 * they were about a quarter of the initial download — `route/place.ts` alone is
 * 52 kB of source, and it, `pathfinder`, `congestion`, `cleanup`, `planes` and
 * `connect` all drag in `route/grid.ts` and `route/astar.ts` behind them. Every
 * one of them is reached from a menu command and from nowhere else.
 *
 * One module rather than ten import sites: they arrive as a single chunk, so a
 * board command pays one request instead of a cascade, and the second command
 * pays nothing because the browser has it. `boardwork()` is safe to call as
 * often as you like — the import is cached by the module loader, and the
 * promise is held here so two commands in flight at once do not both fetch it.
 *
 * The rule for what belongs here: anything only a *command* can reach. The
 * renderer must never need it, or the first frame waits on a network round
 * trip. `net/pour` qualifies despite the name — the renderer draws a pour from
 * the stored fill and never calls `pourGeometry` itself.
 */

let pending: Promise<BoardWork> | null = null;

export interface BoardWork {
  autoPlace: typeof import('@sircad/core/route/place.ts')['autoPlace'];
  applyPlacement: typeof import('@sircad/core/route/place.ts')['applyPlacement'];
  routeConnection: typeof import('@sircad/core/route/connect.ts')['routeConnection'];
  routingLayers: typeof import('@sircad/core/route/netclass.ts')['routingLayers'];
  formatRouteLog: typeof import('@sircad/core/route/pathfinder.ts')['formatRouteLog'];
  cleanup: typeof import('@sircad/core/route/cleanup.ts')['cleanup'];
  planeCandidates: typeof import('@sircad/core/route/planes.ts')['planeCandidates'];
  planeLayersOf: typeof import('@sircad/core/route/planes.ts')['planeLayersOf'];
  pourPlanes: typeof import('@sircad/core/route/planes.ts')['pourPlanes'];
  formatPlaneReport: typeof import('@sircad/core/route/planes.ts')['formatPlaneReport'];
  congestionReport: typeof import('@sircad/core/route/congestion.ts')['congestionReport'];
  formatCongestion: typeof import('@sircad/core/route/congestion.ts')['formatCongestion'];
  courtyardsOf: typeof import('@sircad/core/route/courtyard.ts')['courtyardsOf'];
  floodLayer: typeof import('@sircad/core/net/flood.ts')['floodLayer'];
  removeFlood: typeof import('@sircad/core/net/flood.ts')['removeFlood'];
  pourGeometry: typeof import('@sircad/core/net/pour.ts')['pourGeometry'];
  resolvePour: typeof import('@sircad/core/net/pour.ts')['resolvePour'];
}

export function boardwork(): Promise<BoardWork> {
  pending ??= (async (): Promise<BoardWork> => {
    const [place, connect, netclass, pathfinder, cleanup, planes, congestion,
      courtyard, flood, pour] = await Promise.all([
      import('@sircad/core/route/place.ts'),
      import('@sircad/core/route/connect.ts'),
      import('@sircad/core/route/netclass.ts'),
      import('@sircad/core/route/pathfinder.ts'),
      import('@sircad/core/route/cleanup.ts'),
      import('@sircad/core/route/planes.ts'),
      import('@sircad/core/route/congestion.ts'),
      import('@sircad/core/route/courtyard.ts'),
      import('@sircad/core/net/flood.ts'),
      import('@sircad/core/net/pour.ts'),
    ]);
    return {
      autoPlace: place.autoPlace,
      applyPlacement: place.applyPlacement,
      routeConnection: connect.routeConnection,
      routingLayers: netclass.routingLayers,
      formatRouteLog: pathfinder.formatRouteLog,
      cleanup: cleanup.cleanup,
      planeCandidates: planes.planeCandidates,
      planeLayersOf: planes.planeLayersOf,
      pourPlanes: planes.pourPlanes,
      formatPlaneReport: planes.formatPlaneReport,
      congestionReport: congestion.congestionReport,
      formatCongestion: congestion.formatCongestion,
      courtyardsOf: courtyard.courtyardsOf,
      floodLayer: flood.floodLayer,
      removeFlood: flood.removeFlood,
      pourGeometry: pour.pourGeometry,
      resolvePour: pour.resolvePour,
    };
  })();
  return pending;
}
